import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MarkdownField } from "@/components/ui/markdown-field";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { createScriptureNote } from "@/lib/bible-api";
import { useAuth } from "@/hooks/use-auth";

interface ScriptureDisplayProps {
  /** Display string, optionally "Reference - text" */
  reference: string;
  /** scripture_references.id — required to save a note */
  scriptureReferenceId?: number;
  documentId?: number;
  onNoteSaved?: () => void;
}

export function ScriptureDisplay({
  reference,
  scriptureReferenceId,
  onNoteSaved,
}: ScriptureDisplayProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [note, setNote] = useState("");
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [heading, text] = reference.includes(" - ")
    ? reference.split(" - ")
    : [reference, ""];

  const addNote = async () => {
    if (!note.trim()) {
      toast({
        title: "Note is empty",
        description: "Please enter a note to save.",
        variant: "destructive",
      });
      return;
    }

    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to add notes.",
        variant: "destructive",
      });
      return;
    }

    if (!scriptureReferenceId) {
      toast({
        title: "Missing reference",
        description: "Save this passage first, then add a note.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      await createScriptureNote({
        content: note.trim(),
        user_id: user.id,
        parent_id: scriptureReferenceId,
        parent_type: "scripture",
      });

      toast({
        title: "Note saved",
        description: "Your note has been saved successfully.",
      });

      setNote("");
      setShowNoteInput(false);
      onNoteSaved?.();
    } catch (err) {
      console.error("Error saving note:", err);
      toast({
        title: "Failed to save note",
        description: err instanceof Error ? err.message : "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{heading}</CardTitle>
      </CardHeader>
      <CardContent>
        {text ? (
          <div className="prose prose-sm dark:prose-invert">
            <p>{text}</p>
          </div>
        ) : null}
        <div className="mt-4 flex justify-end space-x-2">
          {showNoteInput ? (
            <div className="w-full space-y-2">
              <MarkdownField
                value={note}
                onValueChange={setNote}
                placeholder="Add your notes here..."
              />
              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNoteInput(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void addNote()}
                  disabled={isSaving || !scriptureReferenceId}
                >
                  {isSaving ? "Saving..." : "Save Note"}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowNoteInput(true)}
              disabled={!scriptureReferenceId}
              title={
                scriptureReferenceId
                  ? "Add a note"
                  : "Save the passage before adding a note"
              }
            >
              Add Note
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
